import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ContractLined } from '@tag/tag-icons';

import PageHeader from '@/components/common/PageHeader/PageHeader';
import {
  ContractBillingStatusPascalCaseEnum,
  ContractStatusEnum
} from '@/graphql/contracts/_generated_/contracts';
import PrevNextButtons from './PrevNextButtons.container';
import { ContractCommonData } from '@/graphql/contracts/_generated_/ContractCommonData.fragment';
import useGetPaginatedBCContractLines
  from '@/pages/ContractDetails/ContractLines/hooks/useGetPaginatedBCContractLines';
import useGetVariableContractLines
  from '@/pages/ContractDetails/ContractLines/hooks/useGetVariableContractLines';
import { VARIABLE_TYPE } from '@/config/statuses.config';

export type OneEvoMigrationData = {
  oneEvoMigrated: boolean;
  oneEvoContractNumber?: string | null;
  oneEvoMigrationDate?: string | null;
};

export type PausedBiilingData = {
  isPaused: boolean;
  pausedBillingDate: Date | null;
  isDateConsistent?: boolean;
};

interface IContractTitle {
  contract: ContractCommonData | undefined;
  isContractLoading: boolean;
}

export default function ContractTitleContainer({ contract, isContractLoading }: IContractTitle) {
  const { contractId }: { contractId: UUID } = useParams();
  const contractHasVariableType = contract?.billingType?.includes(VARIABLE_TYPE);

  const {
    getPaginatedBCContractLines,
    paginatedBCContractLines
  } = useGetPaginatedBCContractLines();

  const {
    getVariableContractLines,
    variableContractLines
  } = useGetVariableContractLines();

  useEffect(() => {
    if (contractId) {
      getPaginatedBCContractLines({ contractId: { eq: contractId } });
    }
  }, [contractId]);

  useEffect(() => {
    if (contractHasVariableType && contractId) {
      getVariableContractLines({ headerPrimaryKey: { eq: contractId } });
    }
  }, [contract?.contractID, contractHasVariableType]);

  const pausedBillingData: PausedBiilingData = useMemo(() => {
    const relevantStatuses = [
      ContractStatusEnum.Active,
      ContractStatusEnum.ActiveDNR,
      ContractStatusEnum.Terminating
    ];

    const relevantBCLines = paginatedBCContractLines.filter(
      (line) => line?.status && relevantStatuses.includes(line.status)
    );

    const relevantVariableLines = variableContractLines.filter(
      (line) => line?.status && relevantStatuses.includes(line.status)
    );

    const allRelevantLines = [...relevantBCLines, ...relevantVariableLines];

    if (allRelevantLines.length === 0) {
      return {
        isPaused: false,
        pausedBillingDate: null,
        isDateConsistent: false
      };
    }

    const allLinesPausedOrFuturePause = allRelevantLines.every(
      (line) => line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.Paused ||
        line?.lineBillingStatus === ContractBillingStatusPascalCaseEnum.FuturePause
    );

    if (!allLinesPausedOrFuturePause) {
      return {
        isPaused: false,
        pausedBillingDate: null,
        isDateConsistent: false
      };
    }

    const pauseEndDates = allRelevantLines
      .map((line) => line?.lineBillingCurrentStatusEndDate)
      .filter((date): date is string => date != null);

    const uniquePauseEndDates = [...new Set(pauseEndDates)];
    const isDateConsistent = uniquePauseEndDates.length === 1;

    const pauseEndDate = isDateConsistent && uniquePauseEndDates[0]
      ? new Date(uniquePauseEndDates[0])
      : null;

    return {
      isPaused: true,
      pausedBillingDate: pauseEndDate,
      isDateConsistent
    };
  }, [
    contract?.billing.status, 
    contract?.billing.currentStatusEndDate,
    paginatedBCContractLines,
    variableContractLines
  ]);

  return (
    <div className="pt-20 mb-20 d-flex justify-content-between">
      <PageHeader
        isLoading={isContractLoading}
        title={contract?.customer?.customerName as string}
        icon={<ContractLined />}
        status={contract?.status}
        billingType={contract?.billingType}
        pausedBillingData={pausedBillingData}
        oneEvoMigration={{
          oneEvoMigrated: Boolean(contract?.oneEvoMigrated),
          oneEvoContractNumber: contract?.oneEvoContractNumber,
          oneEvoMigrationDate: contract?.oneEvoMigrationDate
        }}
      />
      <PrevNextButtons className='d-flex gap-2' contractId={contractId} />
    </div>
  );
}
